import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { 
  EnvelopeIcon, 
  PhoneIcon, 
  MapPinIcon,
  ArrowRightIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const gradientShift = keyframes`
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
`;

const FooterWrapper = styled.footer`
  position: relative;
  background: linear-gradient(135deg, #1a1f2e 0%, #2d3748 100%);
  color: var(--white);
  padding: 80px 0 0;
  margin-top: 100px;
  overflow: hidden;

  /* Decorative top border */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, 
      transparent, 
      var(--davinci-blue), 
      var(--davinci-beach), 
      var(--davinci-blue), 
      transparent
    );
    background-size: 200% 100%;
    animation: ${gradientShift} 3s ease infinite;
  }

  /* Background pattern */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      radial-gradient(circle at 20% 80%, rgba(11, 152, 206, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(49, 110, 168, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const FooterContainer = styled.div`
  position: relative;
  z-index: 1;
`;

const FooterTop = styled.div`
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 60px;
  margin-bottom: 40px;
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;

  @media (min-width: 768px) {
    grid-template-columns: 1.5fr 1fr 1fr 1fr;
    gap: 60px;
  }
`;

const BrandSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const LogoContainer = styled.div`
  margin-bottom: 16px;
`;

const LogoImage = styled.img`
  height: 48px;
  width: auto;
  filter: brightness(0) invert(1);
  animation: ${float} 6s ease-in-out infinite;
`;

const BrandDescription = styled.p`
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.7;
  font-size: 0.95rem;
  margin: 0;
`;

const Newsletter = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
  margin-top: 8px;
`;

const NewsletterTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  margin: 0 0 12px 0;
  color: white;

  svg {
    width: 20px;
    height: 20px;
    color: var(--davinci-beach);
  }
`;

const NewsletterForm = styled.form`
  display: flex;
  gap: 8px;
`;

const NewsletterInput = styled.input`
  flex: 1;
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: white;
  font-size: 0.9rem;
  transition: all 0.3s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.15);
    border-color: var(--davinci-beach);
    box-shadow: 0 0 0 3px rgba(11, 152, 206, 0.1);
  }
`;

const NewsletterButton = styled.button`
  padding: 10px 20px;
  background: linear-gradient(135deg, var(--davinci-blue), var(--davinci-beach));
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  min-width: 140px;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(11, 152, 206, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.8;
  }
`;

const FooterColumn = styled.div`
  h3 {
    font-size: 0.875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: rgba(255, 255, 255, 0.5);
    margin: 0 0 20px 0;
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FooterLink = styled(Link)`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.95rem;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  position: relative;
  padding-left: 0;

  &::before {
    content: '';
    position: absolute;
    left: -20px;
    width: 12px;
    height: 1px;
    background: var(--davinci-beach);
    opacity: 0;
    transition: all 0.3s ease;
  }

  &:hover {
    color: white;
    padding-left: 8px;

    &::before {
      left: -8px;
      opacity: 1;
    }
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
  color: rgba(255, 255, 255, 0.8);

  svg {
    width: 18px;
    height: 18px;
    color: var(--davinci-beach);
    flex-shrink: 0;
    margin-top: 2px;
  }

  a {
    color: rgba(255, 255, 255, 0.9);
    transition: color 0.3s ease;

    &:hover {
      color: var(--davinci-beach);
    }
  }
`;

const FooterBottom = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 32px 0;

  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const Copyright = styled.div`
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.875rem;

  @media (min-width: 768px) {
    text-align: left;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 16px;
`;

const SocialLink = styled.a`
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 600;

  &:hover {
    background: var(--davinci-blue);
    border-color: var(--davinci-blue);
    color: white;
    transform: translateY(-3px);
    box-shadow: 0 8px 24px rgba(11, 152, 206, 0.4);
  }
`;



const SocialIcon = styled.svg`
  width: 20px;
  height: 20px;
  fill: currentColor;
`;

// Add spinner animation
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const FeedbackMessage = styled.div<{ type: 'success' | 'warning' | 'error' | '' }>`
  margin-top: 12px;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.875rem;
  line-height: 1.4;
  text-align: center;
  transition: all 0.3s ease;
  
  ${props => props.type === 'success' && `
    background: rgba(16, 185, 129, 0.1);
    border: 1px solid rgba(16, 185, 129, 0.3);
    color: #10b981;
  `}
  
  ${props => props.type === 'warning' && `
    background: rgba(251, 191, 36, 0.1);
    border: 1px solid rgba(251, 191, 36, 0.3);
    color: #f59e0b;
  `}
  
  ${props => props.type === 'error' && `
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #ef4444;
  `}
`;

const FooterEnhanced: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'warning' | 'error' | ''; message: string }>({ type: '', message: '' });

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setFeedback({ type: '', message: '' });
    
    try {
      const response = await fetch('/api/newsletter', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Use the message from backend which differs for new vs existing subscribers
        // Show yellow warning for existing subscribers, green success for new
        setFeedback({
          type: data.is_new ? 'success' : 'warning',
          message: data.message || 'Successfully subscribed!'
        });
        
        // Only clear email for new subscribers
        if (data.is_new) {
          setEmail('');
        }
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setFeedback({ type: '', message: '' });
        }, 5000);
      } else {
        setFeedback({
          type: 'error',
          message: data.error || 'Something went wrong. Please try again.'
        });
      }
    } catch (error) {
      setFeedback({
        type: 'error',
        message: 'Unable to connect. Please check your connection and try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FooterWrapper>
      <FooterContainer className="container">
        <FooterTop>
          <FooterGrid>
            <BrandSection>
              <LogoContainer>
                <LogoImage src="/logos/davinci-logo-white.svg" alt="Davinci AI Solutions" />
              </LogoContainer>
              
              <BrandDescription>
                Calgary's AI implementation experts. We don't replace your people—we give them superpowers. 
                Real solutions that work Monday morning, not PowerPoint promises.
              </BrandDescription>

              <Newsletter>
                <NewsletterTitle>
                  <SparklesIcon />
                  Weekly AI Implementation Tips
                </NewsletterTitle>
                <NewsletterForm onSubmit={handleNewsletterSubmit}>
                  <NewsletterInput 
                    type="email"
                    placeholder="your@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <NewsletterButton type="submit" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <Spinner />
                        <span>Subscribing...</span>
                      </>
                    ) : (
                      <>
                        Subscribe
                        <ArrowRightIcon />
                      </>
                    )}
                  </NewsletterButton>
                </NewsletterForm>
                {feedback.message && (
                  <FeedbackMessage type={feedback.type}>
                    {feedback.message}
                  </FeedbackMessage>
                )}
              </Newsletter>
            </BrandSection>

            <FooterColumn>
              <h3>Solutions</h3>
              <FooterLinks>
                <li><FooterLink to="/services/ai-solutions">AI Team Augmentation</FooterLink></li>
                <li><FooterLink to="/services/it-projects">Infrastructure Projects</FooterLink></li>
                <li><FooterLink to="/services/managed-it">AI-Powered IT</FooterLink></li>
                <li><FooterLink to="/services/helpdesk">Superhero Support</FooterLink></li>
              </FooterLinks>
            </FooterColumn>

            <FooterColumn>
              <h3>Company</h3>
              <FooterLinks>
                <li><FooterLink to="/about">About Us</FooterLink></li>
                <li><FooterLink to="/services">All Services</FooterLink></li>
                <li><FooterLink to="/contact">Contact</FooterLink></li>
                <li><FooterLink to="/terms">Terms of Use</FooterLink></li>
                <li><FooterLink to="/privacy">Privacy Policy</FooterLink></li>
                <li><FooterLink to="/security">Security</FooterLink></li>
                <li><FooterLink to="/eula">EULA</FooterLink></li>
              </FooterLinks>
            </FooterColumn>

            <FooterColumn>
              <h3>Get in Touch</h3>
              <ContactItem>
                <MapPinIcon />
                <div>
                  11-6320 11 Street SE<br />
                  Calgary, AB T2H 2L7<br />
                  Canada
                </div>
              </ContactItem>
              <ContactItem>
                <EnvelopeIcon />
                <a href="mailto:info@davincisolutions.ai">
                  info@davincisolutions.ai
                </a>
              </ContactItem>
              <ContactItem>
                <PhoneIcon />
                <a href="tel:+14032459429">
                  (403) 245-9429
                </a>
              </ContactItem>
            </FooterColumn>
          </FooterGrid>
        </FooterTop>

        <FooterBottom>
          <Copyright>
            <div>© {new Date().getFullYear()} Davinci AI Solutions (HL Networks Inc.)</div>
            <div>Empowering Calgary businesses with AI since 2019</div>
          </Copyright>
          <SocialLinks>
            <SocialLink 
              href="https://linkedin.com/company/davinci-ai-solutions"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
            >
              <SocialIcon viewBox="0 0 24 24">
                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
              </SocialIcon>
            </SocialLink>
            <SocialLink 
              href="https://twitter.com/davinciaisolutions"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Twitter"
            >
              <SocialIcon viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </SocialIcon>
            </SocialLink>
            <SocialLink 
              href="https://github.com/Davinci-Technology"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GitHub"
            >
              <SocialIcon viewBox="0 0 24 24">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
              </SocialIcon>
            </SocialLink>
          </SocialLinks>
        </FooterBottom>
      </FooterContainer>
    </FooterWrapper>
  );
};

export default FooterEnhanced;